/**
 * Building-management modules are pack-agnostic.
 * Any BMS template (skyscraper, education, retail, custom, …) can enable them
 * via LocationTypeDefinition / section visibility flags.
 *
 * UI grouping: sidebar section "Building management" (shown in Management and
 * Campus Life view modes so education packs can use the same modules).
 */

export type BuildingManagementModuleId =
  | "documents"
  | "work_orders"
  | "schedule"
  | "safety"
  | "upkeep";

export interface BuildingManagementModule {
  id: BuildingManagementModuleId;
  /** LocationTypeDefinition / override boolean field */
  sectionKey:
    | "showBuildingDocument"
    | "showWorkOrder"
    | "showBuildingSchedule"
    | "showBuildingSafety"
    | "showUpkeepRequest";
  label: string;
  description: string;
  path: string;
  /** ContentType used when the module stores Content rows */
  contentType?:
    | "BUILDING_DOCUMENT"
    | "WORK_ORDER"
    | "BUILDING_SCHEDULE"
    | "UPKEEP_REQUEST";
  /** Document categories for the Documents module */
  documentCategories?: string[];
}

export const BUILDING_MANAGEMENT_MODULES: BuildingManagementModule[] = [
  {
    id: "documents",
    sectionKey: "showBuildingDocument",
    label: "Documents",
    description:
      "Exit plans, blueprints, rules, safety certificates, and other official files.",
    path: "/building-documents",
    contentType: "BUILDING_DOCUMENT",
    documentCategories: [
      "EXIT_PLAN",
      "BLUEPRINT",
      "RULES",
      "SAFETY",
      "CERTIFICATE",
      "OTHER",
    ],
  },
  {
    id: "work_orders",
    sectionKey: "showWorkOrder",
    label: "Work orders",
    description:
      "Tenant maintenance and access requests handled by building ops.",
    path: "/work-orders",
    contentType: "WORK_ORDER",
  },
  {
    id: "schedule",
    sectionKey: "showBuildingSchedule",
    label: "Ops schedule",
    description:
      "Planned shutdowns, elevator maintenance, drills, and inspections.",
    path: "/building-schedule",
    contentType: "BUILDING_SCHEDULE",
  },
  {
    id: "safety",
    sectionKey: "showBuildingSafety",
    label: "Safety hub",
    description:
      "Emergency contacts, muster points, and active safety guidance.",
    path: "/building-safety",
  },
  {
    id: "upkeep",
    sectionKey: "showUpkeepRequest",
    label: "Cleaning & Upkeep",
    description:
      "Members report spills, washroom cleaning, trash, and other upkeep needs.",
    path: "/upkeep",
    contentType: "UPKEEP_REQUEST",
  },
];

export const UPKEEP_CATEGORY_LABELS: Record<string, string> = {
  SPILL: "Spill",
  WASHROOM: "Washroom cleaning",
  TRASH: "Trash / overflowing bins",
  CLEANING: "General cleaning",
  SUPPLIES: "Out of supplies (soap, paper…)",
  PEST: "Pests",
  OTHER: "Other",
};

export const UPKEEP_STATUSES = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"] as const;
export type UpkeepStatus = (typeof UPKEEP_STATUSES)[number];

export const UPKEEP_STATUS_LABELS: Record<UpkeepStatus, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

export const BUILDING_MANAGEMENT_SECTION_KEYS =
  BUILDING_MANAGEMENT_MODULES.map((m) => m.sectionKey);

export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  EXIT_PLAN: "Emergency exit plan",
  BLUEPRINT: "Blueprint / floor plan",
  RULES: "Building rules",
  SAFETY: "Safety plan",
  CERTIFICATE: "Certificate",
  OTHER: "Other document",
};
